import {ArrowLeftRight, Check, Loader2} from 'lucide-react'
import {useEffect, useMemo, useState} from 'react'
import {Button} from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {type BadgeTone, TintBadge} from '@/components/ui/tint-badge'
import {formatUserName} from '@/features/permits/utils/formatters'
import type {DevUser} from '@/lib/api/dev'
import {getDevUsers} from '@/lib/api/dev'
import {cn} from '@/lib/utils'
import {useImpersonationStore} from '@/stores/impersonation-store'

const NO_ORG_KEY = '__no_org__'

// ADMIN was `bg-amber-500/15 text-amber-700` — a raw-palette escape with no
// dark-mode variant, so the label was unreadable on the dark canvas. Tokens
// carry their own dark values, and this now matches how the Users table
// colors the same enum (brand for ADMIN, info for DEVELOPER).
const ROLE_BADGE_TONE: Record<string, BadgeTone> = {
	DEVELOPER: 'info',
	ADMIN: 'brand',
	USER: 'neutral',
}

function UserAvatar({user}: {user: DevUser}) {
	if (user.avatarUrl) {
		return (
			<img
				src={user.avatarUrl}
				alt=''
				className='size-7 rounded-full object-cover'
			/>
		)
	}

	const initials = [user.firstName?.[0], user.lastName?.[0]]
		.filter(Boolean)
		.join('')

	return (
		<div className='flex size-7 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground'>
			{initials || '?'}
		</div>
	)
}

export function DevUserSwitcherContent({autoLoad}: {autoLoad: boolean}) {
	const [users, setUsers] = useState<DevUser[]>([])
	const [loading, setLoading] = useState(false)
	const target = useImpersonationStore((s) => s.target)
	const setTarget = useImpersonationStore((s) => s.setTarget)

	useEffect(() => {
		if (!autoLoad || users.length > 0) return

		let cancelled = false
		setLoading(true)

		getDevUsers()
			.then((response) => {
				if (!cancelled) setUsers(response.data)
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [autoLoad, users.length])

	const groupedByOrg = useMemo(() => {
		const groups = new Map<string, {name: string; users: DevUser[]}>()
		for (const user of users) {
			const orgId = user.organization?.id ?? NO_ORG_KEY
			const baseName = user.organization?.name ?? 'No organization'
			const orgName = user.organization?.archivedAt
				? `${baseName} (archived)`
				: baseName
			const existing = groups.get(orgId)
			if (existing) {
				existing.users.push(user)
			} else {
				groups.set(orgId, {name: orgName, users: [user]})
			}
		}
		const noOrgGroup = groups.get(NO_ORG_KEY)
		groups.delete(NO_ORG_KEY)
		return noOrgGroup ? [noOrgGroup, ...groups.values()] : [...groups.values()]
	}, [users])

	function handleSwitch(targetUser: DevUser): void {
		const isAlreadySelected = targetUser.id === target?.id
		setTarget(isAlreadySelected ? null : targetUser)
		// Identity-scoped state lives in the query cache AND the URL: e.g. the
		// board writes the active cycle id into `?sprintId`, and carrying the
		// previous org's id into the next identity freezes its board read-only
		// (resolveSprintMode treats any non-active id as a closed cycle). A full
		// reload on the bare pathname resets both in one stroke.
		window.location.assign(window.location.pathname)
	}

	return (
		<Command>
			<CommandInput placeholder='Search users...' />
			<CommandList className='max-h-[500px]'>
				{loading && (
					<div className='flex items-center justify-center py-6'>
						<Loader2 className='size-4 animate-spin text-muted-foreground' />
					</div>
				)}
				<CommandEmpty>No users found.</CommandEmpty>
				{groupedByOrg.map((group) => (
					<CommandGroup key={group.name} heading={group.name}>
						{group.users.map((user) => {
							const isActive = user.id === target?.id

							return (
								<CommandItem
									key={user.id}
									value={`${formatUserName(user)} ${user.email ?? ''}`}
									onSelect={() => handleSwitch(user)}
								>
									<UserAvatar user={user} />
									<div className='min-w-0 flex-1'>
										<div className='flex items-center gap-1.5'>
											<span className='truncate text-sm font-medium'>
												{formatUserName(user)}
											</span>
											<TintBadge
												tone={ROLE_BADGE_TONE[user.role] ?? 'neutral'}
												size='xs'
											>
												{user.role}
											</TintBadge>
										</div>
										{user.email && (
											<p className='truncate text-xs text-muted-foreground'>
												{user.email}
											</p>
										)}
									</div>
									{isActive && (
										<Check className='size-4 text-[var(--mat-primary)]' />
									)}
								</CommandItem>
							)
						})}
					</CommandGroup>
				))}
			</CommandList>
		</Command>
	)
}

export function DevUserSwitcher({className}: {className?: string}) {
	const [open, setOpen] = useState(false)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant='ghost'
					size='icon'
					className={cn('size-8', className)}
					title='Switch user (dev)'
				>
					<ArrowLeftRight className='size-4' />
				</Button>
			</PopoverTrigger>
			<PopoverContent className='w-80 p-0' align='end'>
				<DevUserSwitcherContent autoLoad={open} />
			</PopoverContent>
		</Popover>
	)
}
